/**
 * dopamiles-cart.js — cart drawer open/close, AJAX qty, upsell ATC, cart page qty.
 * Fires 'cart:change' custom event after every mutation.
 *
 * Phase 05: moved swapSection / updateBubbleCount / publishCartUpdate /
 * applyCartMutation to helpers.js + mutations.js. Dawn-style ATC (drawer
 * opens after fetch). dataset.bound dedupe. 422 error surfacing.
 *
 * Depends on (defer-ordered before this file):
 *   dopamiles-cart-helpers.js   → window.bncCartHelpers
 *   dopamiles-cart-mutations.js → window.bncCartMutations
 */

(function () {
  'use strict';

  // ── Dependency guard ──────────────────────────────────────────────────────

  if (!window.bncCartHelpers) {
    console.warn('[bnc-cart] bncCartHelpers not loaded — cart will not function');
  }
  if (!window.bncCartMutations) {
    console.warn('[bnc-cart] bncCartMutations not loaded — cart mutations will not function');
  }

  // ── Constants ─────────────────────────────────────────────────────────────

  const DRAWER_ID       = 'bnc-cart-drawer';
  const OVERLAY_ID      = 'bnc-cart-overlay';
  const CLOSE_BTN_ID    = 'bnc-cart-close';
  const SECTION_ID      = 'dopamiles-cart-drawer'; // matches section file name
  const PAGE_SECTION_ID = 'dopamiles-cart-main';   // cart page section
  const LOADING_CLASS   = 'bnc-cart-loading';

  // ── Error banner ──────────────────────────────────────────────────────────

  // Surface server error messages into #bnc-cart-err-text inside the banner
  // (auto-clears 5s). Writing to the OUTER #bnc-cart-err-banner would clobber
  // the inline SVG icon next to the text — write to the inner #bnc-cart-err-text
  // div only. Uses escapeHtml before innerHTML. Skips silently if missing.
  function showCartErrBanner(msg) {
    const banner = document.getElementById('bnc-cart-err-banner');
    const textEl = document.getElementById('bnc-cart-err-text');
    if (!banner || !textEl) {
      console.warn('[bnc-cart] err-banner missing, suppressed error:', msg);
      return;
    }
    const safe = window.bncCartHelpers
      ? window.bncCartHelpers.escapeHtml(msg)
      : msg.replace(/</g, '&lt;');
    textEl.innerHTML = safe;
    banner.removeAttribute('hidden');
    clearTimeout(banner._dismiss);
    banner._dismiss = setTimeout(() => {
      textEl.innerHTML = '';
      banner.setAttribute('hidden', '');
    }, 5000);
  }

  // ── Fetch helpers ─────────────────────────────────────────────────────────

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
    if (!res.ok) throw new Error(`fetchCart failed: ${res.status}`);
    return res.json();
  }

  // Cart-mutation serializer. Concurrent /cart/change.js requests across
  // multiple line items race on the server — Shopify processes them in
  // some order but client-side the LAST RESPONSE TO ARRIVE wins the UI
  // render, which may not match the LAST REQUEST FIRED. With unequal
  // network latency between calls, the client UI can permanently diverge
  // from the cart server state (e.g., fire A+, B+, A- with B+ arriving
  // last → client shows post-B+ state, but server has post-A- state).
  //
  // Serialize every mutation through one chained promise so we never have
  // more than one /cart/change.js or /cart/add.js fetch in flight at once.
  // Each link await the previous link's result, even when that result was
  // a rejection — the queue must survive individual failures so a sold-out
  // line doesn't deadlock the rest of the cart.
  let cartMutationChain = Promise.resolve();
  function queueCartMutation(fn) {
    const next = cartMutationChain.then(fn, fn);
    // Swallow rejections on the chain itself so a failed mutation doesn't
    // block subsequent calls. Callers still receive their own rejection.
    cartMutationChain = next.catch(() => {});
    return next;
  }

  // Change qty of a line item. Bundles drawer + page sections (1 round-trip).
  // On 422: surfaces error to banner, then throws so callers can react.
  // Serialized through queueCartMutation to prevent cross-line races.
  function changeCartItem(key, qty) {
    return queueCartMutation(async () => {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty, sections: [SECTION_ID, PAGE_SECTION_ID] }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg  = body.message || body.description || 'Unable to update cart';
        showCartErrBanner(msg);
        throw new Error(`changeCartItem failed: ${res.status} — ${msg}`);
      }
      const data = await res.json();
      return { cart: data, sections: data.sections || {} };
    });
  }

  // Build a clean /cart/add line item, carrying line-item properties and a
  // selling_plan only when present. The product form may attach `properties[…]`
  // (e.g. uploaded artwork on a POD product, gift-card recipient fields) or a
  // selling_plan; dropping them would charge the order yet lose the data the
  // fulfilment depends on.
  function cleanLine(id, quantity, extra) {
    const line = { id: parseInt(id, 10), quantity: quantity };
    if (extra && extra.properties && Object.keys(extra.properties).length) line.properties = extra.properties;
    if (extra && extra.selling_plan) line.selling_plan = extra.selling_plan;
    return line;
  }

  // Add variant to cart. Bundles drawer section to skip extra round-trip.
  // /cart/add.js returns line item, not full cart — one extra /cart.js fetch needed.
  // Serialized via queueCartMutation — adds also hit shared cart state.
  function addToCart(variantId, qty = 1, extra = null) {
    return queueCartMutation(async () => {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...cleanLine(variantId, qty, extra), sections: [SECTION_ID] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || `addToCart failed: ${res.status}`);
      }
      const data = await res.json();
      const cart = await fetchCart();
      return { cart, sections: data.sections || {} };
    });
  }

  // Add multiple variants in a single /cart/add.js call. Used to bundle the
  // Shipping Protection variant into the user-initiated ATC so the drawer
  // opens with both lines already present — no flash of pre-SP total.
  // Serialized via queueCartMutation.
  function addToCartMulti(items) {
    return queueCartMutation(async () => {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items, sections: [SECTION_ID] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || `addToCartMulti failed: ${res.status}`);
      }
      const data = await res.json();
      const cart = await fetchCart();
      return { cart, sections: data.sections || {} };
    });
  }

  // Fallback drawer refresh via Section Rendering API (used by window.bncCart.refresh()).
  // Primary path is applyCartMutation from bundled response; this is a defensive fallback.
  async function refreshDrawer() {
    let res;
    try {
      res = await fetch(`/?sections=${SECTION_ID}`, { headers: { 'Content-Type': 'application/json' } });
    } catch (fetchErr) {
      showCartErrBanner('Could not refresh cart — check your connection.');
      console.error('[bnc-cart] refreshDrawer network error', fetchErr);
      return;
    }
    if (!res.ok) {
      showCartErrBanner('Could not refresh cart — server error.');
      console.error('[bnc-cart] refreshDrawer HTTP error', res.status);
      return;
    }
    const data = await res.json();
    const html = data[SECTION_ID];
    if (!html) return;

    if (window.bncCartMutations) window.bncCartMutations.swapSection(html, 'bnc-cart-drawer-content');
    rebindSurface('drawer');

    // Update count label outside content wrap (can't be reached by swapSection alone).
    const doc = window.bncCartHelpers
      ? window.bncCartHelpers.parseSectionDoc(html)
      : new DOMParser().parseFromString(html, 'text/html');
    if (doc) {
      const newLabel = doc.getElementById('bnc-cart-count-label');
      const curLabel = document.getElementById('bnc-cart-count-label');
      if (newLabel && curLabel) curLabel.innerHTML = newLabel.innerHTML;
    }

    fetchCart().then((cart) => {
      if (window.bncCartHelpers) {
        window.bncCartHelpers.updateBubbleCount(window.bncCartHelpers.displayCount(cart));
        window.bncCartHelpers.publishCartUpdate(cart);
      }
      document.dispatchEvent(new CustomEvent('cart:change', { detail: cart, bubbles: true }));
    }).catch(() => {});
  }

  // ── Loading state ─────────────────────────────────────────────────────────

  function setLoading(isLoading) {
    const content = document.getElementById('bnc-cart-drawer-content');
    if (!content) return;
    content.classList.toggle(LOADING_CLASS, isLoading);
  }

  // ── Drawer open / close ───────────────────────────────────────────────────

  function openDrawer() {
    const drawer  = document.getElementById(DRAWER_ID);
    const overlay = document.getElementById(OVERLAY_ID);
    if (!drawer) return;
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) { overlay.classList.add('open'); overlay.setAttribute('aria-hidden', 'false'); }
    document.body.style.overflow = 'hidden';
    const closeBtn = document.getElementById(CLOSE_BTN_ID);
    if (closeBtn) setTimeout(() => closeBtn.focus(), 50);
    // Fire-and-forget: SP auto-add (gated by once-per-session + opt-out flags).
    maybeAutoAddSP();
  }

  function closeDrawer() {
    const drawer  = document.getElementById(DRAWER_ID);
    const overlay = document.getElementById(OVERLAY_ID);
    if (!drawer) return;
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    if (overlay) { overlay.classList.remove('open'); overlay.setAttribute('aria-hidden', 'true'); }
    document.body.style.overflow = '';
  }

  // ── Mutation ctx helper ───────────────────────────────────────────────────

  // Shared ctx arg passed to applyCartMutation so mutations.js stays pure (no back-ref to cart.js).
  function mutationCtx() {
    return { rebindSurface, sectionId: SECTION_ID, pageSectionId: PAGE_SECTION_ID };
  }

  // ── Qty / remove / upsell handlers ───────────────────────────────────────

  async function handleQtyChange(btn, delta) {
    if (btn.disabled) return;
    const key     = btn.dataset.lineKey;
    const liEl    = btn.closest('.bnc-li');
    const qtySpan = liEl && liEl.querySelector('.bnc-li-qty-val');
    if (!key) return;

    const currentQty = qtySpan ? parseInt(qtySpan.textContent, 10) : 1;
    const newQty     = Math.max(0, currentQty + delta);

    // Lock BOTH qty buttons on this line for the duration of the mutation.
    // Each click derives its target from the rendered quantity; a second click
    // before the line swap completes would compute off the stale base and
    // silently lose an update (final qty diverges from the shopper's intent on
    // a rapid double-tap).
    const qtyBtns = liEl ? liEl.querySelectorAll('.bnc-li-qty-inc, .bnc-li-qty-dec') : [btn];
    qtyBtns.forEach((b) => { b.disabled = true; });
    // Honest loading state: spinner replaces +/- glyph during fetch.
    // Success path discards DOM (applyCartMutation swaps the line) so
    // no restore needed there; failure path restores below.
    const origHtml = btn.innerHTML;
    btn.innerHTML = '<span class="bnc-btn-spinner" aria-label="Updating quantity"></span>';
    setLoading(true);
    try {
      const result = await changeCartItem(key, newQty);
      if (window.bncCartMutations) window.bncCartMutations.applyCartMutation(result, mutationCtx());
    } catch (err) {
      console.error('[bnc-cart] qty change failed', err);
      qtyBtns.forEach((b) => { b.disabled = false; });
      btn.innerHTML = origHtml;
    } finally {
      setLoading(false);
    }
  }

  async function handleRemove(btn) {
    if (btn.disabled) return;
    const key = btn.dataset.lineKey;
    if (!key) return;
    btn.disabled = true;
    // Honest loading state: spinner replaces "Remove" text during fetch.
    // Same restore pattern as handleQtyChange (only failure path needs it).
    const origHtml = btn.innerHTML;
    btn.innerHTML = '<span class="bnc-btn-spinner" aria-label="Removing item"></span>';
    setLoading(true);
    try {
      const result = await changeCartItem(key, 0);
      if (window.bncCartMutations) window.bncCartMutations.applyCartMutation(result, mutationCtx());
    } catch (err) {
      console.error('[bnc-cart] remove failed', err);
      btn.disabled = false;
      btn.innerHTML = origHtml;
    } finally {
      setLoading(false);
    }
  }

  async function handleUpsellAdd(btn) {
    if (btn.disabled) return;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.disabled = true;
    const origHtml = btn.innerHTML;
    btn.innerHTML = '<span class="bnc-btn-spinner" aria-label="Adding to cart"></span>';
    setLoading(true);
    try {
      const result = await addToCart(variantId, 1);
      if (window.bncCartMutations) window.bncCartMutations.applyCartMutation(result, mutationCtx());
    } catch (err) {
      console.error('[bnc-cart] upsell add failed', err);
      btn.disabled = false;
      btn.innerHTML = origHtml || '+ Add';
    } finally {
      setLoading(false);
    }
  }

  // ── Cart page defensive refresh ───────────────────────────────────────────

  function refreshPageTotals() {
    if (!document.getElementById('bnc-cart-page')) return;
    fetch('/?sections=' + PAGE_SECTION_ID, { headers: { 'Content-Type': 'application/json' } })
      .then((r) => r.json())
      .then((data) => {
        const html = data[PAGE_SECTION_ID];
        if (!html) return;
        const tmp = window.bncCartHelpers
          ? window.bncCartHelpers.parseSectionDoc(html)
          : new DOMParser().parseFromString(html, 'text/html');
        if (!tmp) return;
        const pairs = [
          ['bnc-page-meta',    'bnc-page-meta'],
          ['bnc-page-lines',   'bnc-page-lines'],
          ['bnc-page-summary', 'bnc-page-summary'],
        ];
        pairs.forEach(([srcId, dstId]) => {
          const src = tmp.getElementById(srcId);
          const dst = document.getElementById(dstId);
          if (src && dst) dst.innerHTML = src.innerHTML;
        });
        rebindSurface('page');
      })
      .catch((e) => console.error('[bnc-cart] refreshPageTotals failed', e));
  }

  // ── Event binding ─────────────────────────────────────────────────────────

  // dataset.bound dedupe: first bind sets data-bound="1"; re-bind after
  // innerHTML swap skips already-bound nodes (new DOM nodes start without it).
  //
  // Scope invariant: ALL selector targets below (.bnc-li-qty-*, .bnc-rm,
  // .bnc-upsell-add) MUST live inside #bnc-cart-drawer-content (the swapped
  // wrap) — that's how dedupe stays correct across mutations. If a new bind
  // target lives outside the swap (e.g. drawer header), it would keep
  // dataset.bound=1 forever and only be bound once, which is fine for static
  // elements but would be a footgun for ones that should re-bind. Today: all
  // targets are inside the swap, so dedupe is correct.
  function bindDrawerSurface() {
    const drawer = document.getElementById(DRAWER_ID);
    if (!drawer) return;
    drawer.querySelectorAll('.bnc-li-qty-dec').forEach((btn) => {
      if (btn.dataset.bound === '1') return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => handleQtyChange(btn, -1));
    });
    drawer.querySelectorAll('.bnc-li-qty-inc').forEach((btn) => {
      if (btn.dataset.bound === '1') return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => handleQtyChange(btn, 1));
    });
    drawer.querySelectorAll('.bnc-rm').forEach((btn) => {
      if (btn.dataset.bound === '1') return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => handleRemove(btn));
    });
    drawer.querySelectorAll('.bnc-upsell-add').forEach((btn) => {
      if (btn.dataset.bound === '1') return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => handleUpsellAdd(btn));
    });
    drawer.querySelectorAll('[data-bnc-sp-toggle]').forEach((cb) => {
      // Reconcile checkbox state with session opt-out flag. The snippet
      // renders the checkbox `checked` whenever default_checked is true,
      // ignoring the client-side opt-out flag (Liquid can't read
      // sessionStorage). Without this reconcile, every uncheck causes the
      // post-mutation re-render to flip the checkbox back to checked.
      const root = cb.closest('[data-bnc-shipping-protect]');
      const inCart = root && root.dataset.spInCart === 'true';
      const optedOut = spGet(SP_OPT_OUT_KEY) === '1';
      if (optedOut && !inCart) {
        cb.checked = false;
      } else if (inCart) {
        cb.checked = true;
      }
      if (cb.dataset.bound === '1') return;
      cb.dataset.bound = '1';
      cb.addEventListener('change', () => handleSPToggle(cb));
    });
  }

  function bindPageSurface() {
    const page = document.getElementById('bnc-cart-page');
    if (!page) return;
    page.querySelectorAll('.bnc-li-qty-dec').forEach((btn) => {
      if (btn.dataset.bound === '1') return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => handleQtyChange(btn, -1));
    });
    page.querySelectorAll('.bnc-li-qty-inc').forEach((btn) => {
      if (btn.dataset.bound === '1') return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => handleQtyChange(btn, 1));
    });
    page.querySelectorAll('.bnc-rm').forEach((btn) => {
      if (btn.dataset.bound === '1') return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => handleRemove(btn));
    });
  }

  function rebindSurface(which) {
    if (which === 'drawer')     bindDrawerSurface();
    else if (which === 'page')  bindPageSurface();
    else if (which === 'all') { bindDrawerSurface(); bindPageSurface(); }
  }

  // ── ATC interceptor ───────────────────────────────────────────────────────

  // Transient inline error below product form submit button. Auto-dismisses 6s.
  function showProductFormError(form, message) {
    let banner = form.querySelector('[data-bnc-pf-err]');
    if (!banner) {
      banner = document.createElement('div');
      banner.setAttribute('data-bnc-pf-err', '');
      banner.className = 'bnc-pf-err';
      banner.style.cssText = 'margin-top:10px;padding:10px 12px;background:#fdebe5;border:1px solid #f5cdc0;border-radius:8px;color:#c84a2a;font-size:13.5px;line-height:1.4;';
      form.appendChild(banner);
    }
    banner.textContent = message;
    clearTimeout(banner._dismiss);
    banner._dismiss = setTimeout(() => banner.remove(), 6000);
  }

  // Extract a line item from a product form's FULL FormData so line-item
  // properties (`properties[…]`) and selling_plan survive the intercepted
  // submit. This interceptor preventDefault+stopPropagation the native submit,
  // so it is the sole add path for every /cart/add form site-wide — cherry-
  // picking only id/quantity would silently drop everything else the customer
  // attached. File-valued properties are skipped (JSON body can't carry a File;
  // uploads must store a URL string in a property), all string values pass.
  function formLineItem(form) {
    const data = new FormData(form);
    const properties = {};
    let sellingPlan = null;
    data.forEach((value, name) => {
      if (typeof value !== 'string') return;
      const m = name.match(/^properties\[(.+)\]$/);
      if (m) properties[m[1]] = value;
      else if (name === 'selling_plan' && value) sellingPlan = value;
    });
    return {
      id: data.get('id'),
      quantity: parseInt(data.get('quantity') || '1', 10),
      properties: properties,
      selling_plan: sellingPlan,
    };
  }

  // Dawn-style ATC: fetch first, populate drawer DOM, THEN open drawer.
  // Drawer appears once with real content — no empty/dimmed interim state.
  // On error: show inline form error, do NOT open drawer.
  function bindAddToCartInterceptor() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      e.preventDefault();
      e.stopPropagation();

      const item  = formLineItem(form);
      const varId = item.id;
      const qty   = item.quantity;
      if (!varId) return;

      const submitBtn = form.querySelector('[type="submit"]');
      const origText  = submitBtn ? submitBtn.innerHTML : '';
      if (submitBtn) {
        submitBtn.disabled  = true;
        submitBtn.innerHTML = '<span class="bnc-btn-spinner" aria-label="Adding to cart"></span>';
      }

      try {
        // If Shipping Protection should auto-add and the user isn't adding
        // the SP variant itself, bundle it into the same /cart/add.js call.
        // Result: drawer opens once with both lines present — no flash of
        // pre-SP total. SP_AUTO_ADDED_KEY locks the once-per-session contract.
        const spVariantId = shouldAutoAddSP();
        const bundleSP = spVariantId && String(spVariantId) !== String(varId);
        let result;
        if (bundleSP) {
          result = await addToCartMulti([
            cleanLine(varId, qty, item),
            { id: parseInt(spVariantId, 10), quantity: 1 },
          ]);
          spSet(SP_AUTO_ADDED_KEY, '1');
        } else {
          result = await addToCart(varId, qty, item);
        }
        // Populate drawer DOM with real line item content.
        if (window.bncCartMutations) window.bncCartMutations.applyCartMutation(result, mutationCtx());
        // Open drawer AFTER content is in place.
        openDrawer();
      } catch (err) {
        console.error('[bnc-cart] add to cart failed', err);
        showProductFormError(form, err.message || 'Could not add to cart.');
        // Do NOT openDrawer — prevents stale-state drawer from Phase 02 round-1 bug.
      } finally {
        if (submitBtn) {
          submitBtn.disabled  = false;
          submitBtn.innerHTML = origText;
        }
      }
    }, { capture: true });

    document.addEventListener('cart:open', () => openDrawer());
  }

  // ── Keyboard trap ─────────────────────────────────────────────────────────

  function bindKeyboardHandlers() {
    document.addEventListener('keydown', (e) => {
      const drawer = document.getElementById(DRAWER_ID);
      if (!drawer || !drawer.classList.contains('open')) return;
      if (e.key === 'Escape') closeDrawer();
    });
  }

  // ── Shipping Protection ───────────────────────────────────────────────────

  // sessionStorage flags scope SP behavior to this browsing session:
  //   SP_OPT_OUT_KEY    — set when shopper toggles SP off; skip auto-add until next session
  //   SP_AUTO_ADDED_KEY — set after first successful auto-add; prevents re-add loop
  // Both clear on next session (new tab + closed window). Safari Private mode
  // may silently fail; behavior degrades to "auto-add fires every drawer open"
  // which is acceptable.
  const SP_OPT_OUT_KEY    = 'dop_sp_opted_out';
  const SP_AUTO_ADDED_KEY = 'dop_sp_auto_added';

  // True while a fire-and-forget SP auto-add is in flight. Guards the
  // toggle-off-during-auto-add race (drawer opens → auto-add starts → shopper
  // unchecks before it commits → SP stays in cart against their wish).
  let spInFlight = false;

  // Safe sessionStorage wrappers — Safari Private throws on .setItem.
  function spGet(key) {
    try { return sessionStorage.getItem(key); } catch (_) { return null; }
  }
  function spSet(key, val) {
    try { sessionStorage.setItem(key, val); } catch (_) { /* no-op */ }
  }
  function spRemove(key) {
    try { sessionStorage.removeItem(key); } catch (_) { /* no-op */ }
  }

  // Checkbox toggle handler. Toggle ON → add SP variant; OFF → remove by
  // variant ID (Shopify /cart/change.js accepts variant ID for `id:`).
  // Re-render swaps the whole drawer content; on failure we re-sync the
  // checkbox to its prior state and let user retry.
  async function handleSPToggle(checkbox) {
    if (checkbox.disabled) return;
    const root = checkbox.closest('[data-bnc-shipping-protect]');
    if (!root) return;
    const variantId = root.dataset.spVariantId;
    if (!variantId) return;

    const turningOn = checkbox.checked;
    const inCart = root.dataset.spInCart === 'true';

    // Defensive guard: toggle-off when SP isn't actually in cart (e.g. server
    // rendered the checkbox checked via default_checked while SP wasn't added
    // yet, or auto-add never fired). Record opt-out, leave UI unchecked,
    // skip the doomed /cart/change.js call.
    if (!turningOn && !inCart) {
      spSet(SP_OPT_OUT_KEY, '1');
      return;
    }

    checkbox.disabled = true;
    setLoading(true);
    try {
      if (turningOn) {
        spRemove(SP_OPT_OUT_KEY);
        const result = await addToCart(variantId, 1);
        if (window.bncCartMutations) window.bncCartMutations.applyCartMutation(result, mutationCtx());
      } else {
        spSet(SP_OPT_OUT_KEY, '1');
        const result = await changeCartItem(String(variantId), 0);
        if (window.bncCartMutations) window.bncCartMutations.applyCartMutation(result, mutationCtx());
      }
    } catch (err) {
      console.error('[bnc-cart] SP toggle failed', err);
      // Revert UI to actual cart state on failure.
      checkbox.checked = !turningOn;
      if (turningOn) spSet(SP_OPT_OUT_KEY, '1'); else spRemove(SP_OPT_OUT_KEY);
      checkbox.disabled = false;
    } finally {
      setLoading(false);
      // Checkbox is usually replaced by re-render; only re-enable if it survived.
      if (document.contains(checkbox)) checkbox.disabled = false;
    }
  }

  // Returns the SP variant ID when auto-add should fire as part of the next
  // cart mutation; null otherwise. Reads server-emitted config off the
  // always-present drawer wrapper so the decision works even when the cart
  // is currently empty and the SP snippet isn't yet in the DOM.
  function shouldAutoAddSP() {
    const drawer = document.getElementById(DRAWER_ID);
    if (!drawer) return null;
    const variantId = drawer.dataset.spVariantId;
    const defaultChecked = drawer.dataset.spDefaultChecked === 'true';
    if (!variantId || !defaultChecked) return null;
    if (spGet(SP_OPT_OUT_KEY) === '1') return null;
    if (spGet(SP_AUTO_ADDED_KEY) === '1') return null;
    return variantId;
  }

  // Once-per-session auto-add. Fires from openDrawer() when:
  //   1. Widget rendered (cart not empty + SP product set in theme editor)
  //   2. defaultChecked = true (merchant setting)
  //   3. No prior opt-out this session
  //   4. Not already auto-added this session
  //   5. SP not already in cart (server confirms via data-sp-in-cart, NOT
  //      the checkbox `checked` state — checked can mean "should be added"
  //      when default_checked=true and SP isn't yet in cart).
  //
  // SP auto-add race history: a "toggle off mid-fetch then fetch silently
  // re-adds" race existed in earlier flows. Commits 7342466 + d3d7f9b
  // resolved the original surfaces — 7342466 fixed the auto-add-never-fired
  // bug + toggle-off error, then d3d7f9b bundled SP into the multi-item ATC
  // so the drawer opens with SP already in cart (no auto-add fetch needed
  // on the first ATC of a session). The cart-icon-direct-open path below
  // still fires maybeAutoAddSP() fire-and-forget — a returning visitor
  // opening the drawer with an existing cart but no SP gets the original
  // brief flash plus a narrow window where toggle-off-during-fetch could
  // race. Accepted trade-off; if that path ever becomes hot, gate this
  // function on a module-level `spInFlight` boolean (set true before fetch,
  // reset in finally, disable the SP checkbox while true). Do NOT add an
  // AbortController + 5s timeout — that introduces a new TOCTOU (server
  // commits at t=4.9s, client aborts at t=5.0s, cart server-state diverges
  // from UI).
  async function maybeAutoAddSP() {
    if (spInFlight) return;
    const root = document.querySelector('[data-bnc-shipping-protect]');
    if (!root) return;
    const variantId = root.dataset.spVariantId;
    const defaultChecked = root.dataset.spDefaultChecked === 'true';
    if (!variantId || !defaultChecked) return;

    if (spGet(SP_OPT_OUT_KEY) === '1') return;
    if (spGet(SP_AUTO_ADDED_KEY) === '1') return;

    const inCart = root.dataset.spInCart === 'true';
    if (inCart) {
      // SP already in cart (server confirms) — lock the flag and skip fetch.
      spSet(SP_AUTO_ADDED_KEY, '1');
      return;
    }

    // Disable the SP checkbox while the auto-add is in flight so a mid-fetch
    // toggle-off can't race the commit. Belt-and-braces: re-read the opt-out
    // flag after the add resolves and undo the add if the shopper opted out
    // during the fetch window (a queued/programmatic toggle could still slip
    // past the disabled checkbox).
    spInFlight = true;
    const checkbox = root.querySelector('[data-bnc-sp-toggle]');
    if (checkbox) checkbox.disabled = true;
    try {
      setLoading(true);
      const result = await addToCart(variantId, 1);
      if (spGet(SP_OPT_OUT_KEY) === '1') {
        // Opted out during the auto-add — remove SP and apply the removal
        // result so the drawer + bubble don't show a stale SP line.
        const undo = await changeCartItem(String(variantId), 0).catch(() => null);
        if (undo && window.bncCartMutations) window.bncCartMutations.applyCartMutation(undo, mutationCtx());
      } else {
        if (window.bncCartMutations) window.bncCartMutations.applyCartMutation(result, mutationCtx());
        spSet(SP_AUTO_ADDED_KEY, '1');
      }
    } catch (err) {
      console.error('[bnc-cart] SP auto-add failed', err);
    } finally {
      spInFlight = false;
      setLoading(false);
      const cb = root.querySelector('[data-bnc-sp-toggle]');
      if (cb && document.contains(cb)) cb.disabled = false;
    }
  }

  // ── Init ──────────────────────────────────────────────────────────────────

  function init() {
    // Portal the overlay + drawer to <body> so they escape the header section
    // group's stacking context. The drawer section is rendered inside
    // {% sections 'header-group' %}, where `.shopify-section-group-header-group
    // { z-index: 4 }` + the header's own z-index trap the drawer BELOW the
    // header regardless of the drawer's own z-index. Moving them to direct
    // body children (like Dawn/Shrine, which render the cart drawer at body
    // level) lets the drawer's z-index resolve against the root context, so it
    // cleanly overlays the header. Idempotent + re-runs on theme-editor reload.
    const drawerEl  = document.getElementById(DRAWER_ID);
    const overlayEl = document.getElementById(OVERLAY_ID);
    if (overlayEl && overlayEl.parentElement !== document.body) document.body.appendChild(overlayEl);
    if (drawerEl  && drawerEl.parentElement  !== document.body) document.body.appendChild(drawerEl);

    const closeBtn = document.getElementById(CLOSE_BTN_ID);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

    const overlay = document.getElementById(OVERLAY_ID);
    if (overlay) overlay.addEventListener('click', closeDrawer);

    rebindSurface('all');
    bindAddToCartInterceptor();
    bindKeyboardHandlers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API for external callers (header cart icon, batch ATC flows).
  // refresh() re-fetches and swaps drawer content (call before open() after
  // programmatic cart mutation, otherwise drawer shows pre-mutation state).
  window.bncCart = { open: openDrawer, close: closeDrawer, refresh: refreshDrawer };

})();
